import { IReportingState } from '@/modules/reporting/store/state';
import { IRootState } from '@/store/state';
import { ActionContext, ActionTree } from 'vuex';
import { ReportingService } from '@/modules/reporting/services';
import * as AT from './actionTypes';
import * as MT from './mutationsTypes';
import {
  GenerateReportRequest,
  ReportResponse,
  DownloadReportRequest,
  GeneratedReportDetails,
  Report,
  Notes
} from '../types';
import { FETCH_ORGANISATION_EXPOSED_FUNDS } from '@/modules/fund/store/actionTypes';
import { Fund, SubFundRequest } from '@/modules/fund/types';
import { PensionScheme } from '@/modules/pensionScheme/types';
import { SubFund } from '@/modules/subFund/types';
import { FundService } from '../../fund/services';

/**
 * --------------------------------------------------
 *  Cost Analysis Actions
 * --------------------------------------------------
 **/
export const ReportingActions: IReportingActions = {
  [AT.GENERATE_REPORT]: (
    { commit },
    generateReportRequest
  ): Promise<ReportResponse> => {
    return ReportingService.generateReport(generateReportRequest).then(
      (rawHtml: any) => {
        commit(MT.SET_REPORT_HTML, rawHtml);
        return rawHtml;
      }
    );
  },
  [AT.GENERATE_EXCEL_REPORT]: (
    { commit },
    generateReportRequest
  ): Promise<ReportResponse> => {
    return ReportingService.generateExcelReport(generateReportRequest).then(
      (rawHtml: any) => {
        commit(MT.SET_REPORT_HTML, rawHtml);
        return rawHtml;
      }
    );
  },
  [AT.DOWNLOAD_REPORT]: (
    { commit },
    downloadReportRequest
  ): Promise<GeneratedReportDetails> => {
    return ReportingService.downloadReport(downloadReportRequest).then(
      (generatedReportDetails: GeneratedReportDetails) => {
        commit(MT.SET_GENERATED_REPORT_DETAILS, generatedReportDetails);
        return generatedReportDetails;
      }
    );
  },
  [AT.FETCH_REPORTS]: ({ commit }, organisationId): Promise<Report[]> => {
    return ReportingService.getReports(organisationId).then(
      (reports: Report[]) => {
        commit(MT.SET_REPORTS, reports);
        return reports;
      }
    );
  },

  [AT.FETCH_COMMENTS_BY_INVESTMENT_ID]: (
    { commit },
    investmentId
  ): Promise<Notes[]> => {
    return ReportingService.getComments(investmentId).then(
      (commentData: Notes[]) => {
        commit(MT.SET_REPORT_COMMENTS, commentData);
        return commentData;
      }
    );
  },

  [AT.FETCH_REPORT_PENSION_SCHEMES]: (
    { commit, dispatch },
    organisationId: string
  ): Promise<Fund[]> => {
    return dispatch(FETCH_ORGANISATION_EXPOSED_FUNDS, {
      orgId: organisationId,
      page: 1,
      limit: -1
    }).then((exposedFunds: Fund[]) => {
      let pensionSchemes = exposedFunds.map(it => ({
        ...it.parent,
        timePeriod: it.timePeriod
      }));

      const filteredPensionSchemes: Fund[] = [];
      const filterTemp = new Map();
      for (const item of pensionSchemes) {
        if (!filterTemp.has(item.id)) {
          filterTemp.set(item.id, true); // set any value to Map
          filteredPensionSchemes.push(item);
        }
      }
      commit(MT.SET_REPORT_PENSION_SCHEMES, filteredPensionSchemes);
      return filteredPensionSchemes;
    });
  },

  [AT.FETCH_EXPOSED_SUB_FUNDS]: (
    { commit },
    params: SubFundRequest
  ): Promise<SubFund[]> => {
    return FundService.getOrganizationSubFunds(params).then(
      (data: SubFund[]) => {
        commit(MT.SET_EXPOSED_SUB_FUNDS, data);
        return data;
      }
    );
  },

  [AT.SET_SCHEMES_HAVING_EXPOSED_SUBFUNDS]: ({
    commit,
    dispatch,
    getters
  }): PensionScheme[] => {
    const allSchemes: PensionScheme[] = getters.filterClients;
    const allSubFundsWithExposedData: SubFund[] = getters.exposedSubFunds;

    const filteredSchemes = allSchemes.filter(ps => {
      if (ps.proportions.length) {
        // Check for schemes which has any proportion in any subfund with exposed data
        return ps.proportions.some(pr =>
          allSubFundsWithExposedData.find(
            sf => sf.id === pr.subfund_investment_id
          )
        );
      }

      return false;
    });

    commit(MT.SET_SCHEMES_HAVING_EXPOSED_SUBFUNDS, filteredSchemes);
    return filteredSchemes;
  },

  [AT.SET_REPORT_DATA_LOADING]: ({ commit }, status: boolean): boolean => {
    commit(MT.SET_LOADING_REPORT_DATA, status);
    return status;
  }
};

/**
 * --------------------------------------------------
 *  Cost Analysis Action Context Interface
 * --------------------------------------------------
 **/
export interface IReportingActionContext
  extends ActionContext<IReportingState, IRootState> {}

/**
 * --------------------------------------------------
 *  Cost Analysis Actions Interface
 * --------------------------------------------------
 **/
export interface IReportingActions
  extends ActionTree<IReportingState, IRootState> {
  [AT.GENERATE_REPORT](
    context: IReportingActionContext,
    generateReportRequest: GenerateReportRequest
  ): Promise<ReportResponse>;

  [AT.GENERATE_EXCEL_REPORT](
    context: IReportingActionContext,
    generateReportRequest: GenerateReportRequest
  ): Promise<ReportResponse>;

  [AT.DOWNLOAD_REPORT](
    context: IReportingActionContext,
    downloadReportRequest: DownloadReportRequest
  ): Promise<GeneratedReportDetails>;

  [AT.FETCH_REPORTS](
    context: IReportingActionContext,
    organisationId: string
  ): Promise<Report[]>;

  [AT.FETCH_COMMENTS_BY_INVESTMENT_ID](
    context: IReportingActionContext,
    investmentId: string
  ): Promise<Notes[]>;

  [AT.FETCH_REPORT_PENSION_SCHEMES](
    context: IReportingActionContext,
    organisationId: string
  ): Promise<Fund[]>;

  [AT.FETCH_EXPOSED_SUB_FUNDS](
    context: IReportingActionContext,
    params: SubFundRequest
  ): Promise<SubFund[]>;

  [AT.SET_SCHEMES_HAVING_EXPOSED_SUBFUNDS](
    context: IReportingActionContext
  ): PensionScheme[];

  [AT.SET_REPORT_DATA_LOADING](
    context: IReportingActionContext,
    status: boolean
  ): boolean;
}
